import {
    Button, IconButton, makeStyles,
} from '@material-ui/core';
import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import AvatarEditor from 'react-avatar-editor';
import { getTranslate } from 'react-localize-redux';
import { useSelector } from 'react-redux';
import classnames from 'classnames';

import {
    AddImageIcon, RotateIcon, ZoomInIcon, ZoomOutIcon,
} from '../../../common/icons';
import apiUrls from '../../../youwell-common/apiUrls';
import { Image } from '../../../youwell-common/components';
import PortalModal from '../../../common/components/PortalModal';
import { youwellFetch } from '../../../youwell-common/fetchUtils';

const canvasBorder = 50;
const useStyles = makeStyles(theme => ({
    root: {
        minHeight: props => props.width + 2 * canvasBorder,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
    },
    upload: {
        display: 'none',
    },
    currentImage: {
        width: props => props.width * 0.7,
        marginBottom: theme.spacing(2),
    },
    uploadButton: {
        marginBottom: theme.spacing(2),
    },
    deleteCurrent: {
        color: theme.palette.error.main,
    },
    editor: {
        border: theme.youwell.borders.dim,
    },
    imageActions: {
        width: props => props.width + 2 * canvasBorder,
        margin: theme.spacing(2, 0),
        display: 'flex',
    },
    flex: {
        flex: '1 1 auto',
    },
    cancelButton: {
        marginRight: theme.spacing(1),
    },
    imageButton: {
        width: '100%',
    },
    noImage: {
        width: '100%',
        height: '100%',
    },
    image: {
        width: '100%',
    },
    addOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        background: theme.palette.grey[100],
        opacity: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',

        '$imageButton:hover &': {
            opacity: 0.7,
        },

        '$imageButton$noImage &': {
            opacity: 1,
        },
    },
    addIcon: {
        width: '30%',
        height: '30%',
    },
}));

const ProfileImageEditor = ({
    width, onSave, onDelete, currentImageId,
}) => {
    const classes = useStyles({ width });
    const translate = useSelector(state => getTranslate(state.localize));
    const [image, setImage] = useState(null);
    const [scale, setScale] = useState(1.2);
    const [rotation, setRotation] = useState(0);
    const [modalOpen, setModalOpen] = useState(false);
    const editor = useRef(null);

    const openModal = () => {
        setModalOpen(true);
    };
    const closeModal = () => {
        setImage(null);
        setRotation(0);
        setScale(1.2);
        setModalOpen(false);
    };

    const handleFileUpload = (event) => {
        const file = event.target.files[0];
        setImage(file);
    };

    const rotate = () => {
        setRotation(prev => prev - 90);
    };
    const scaleImage = direction => () => {
        setScale(prev => prev + (direction * 0.2));
    };

    const onClickSave = () => {
        if (editor.current) {
            // This returns a HTMLCanvasElement, it can be made into a data URL or a blob,
            // drawn on another canvas, or added to the DOM.
            const canvas = editor.current.getImage();

            // If you want the image resized to the canvas size (also a HTMLCanvasElement)
            // const canvas = editor.current.getImageScaledToCanvas();

            // console.log('save', { canvas, canvasScaled });

            const canvasUrl = canvas.toDataURL();
            youwellFetch(canvasUrl)
                .then(res => res.blob())
                .then(blob => onSave(blob, closeModal));
        }
    };

    const cancelEdit = () => {
        setImage(null);
    };

    const deleteCurrent = () => {
        onDelete(closeModal);
    };

    const imageUrl = currentImageId ? `${apiUrls.baseUrl}/${apiUrls.app.user}/image/${currentImageId}` : '';

    return (
        <>
            <PortalModal
                open={modalOpen}
                onClose={closeModal}
                heading={translate('profileImage.setProfileImage')}
            >
                <div className={classes.root}>
                    {!image ? (
                        <>
                            {imageUrl && (
                                <Image src={imageUrl} alt="" className={classes.currentImage} />
                            )}
                            <input
                                accept="image/*"
                                className={classes.upload}
                                onChange={handleFileUpload}
                                type="file"
                                id="avatar_upload"
                            />
                            <label htmlFor="avatar_upload">
                                <Button variant="outlined" color="primary" component="span" className={classes.uploadButton}>
                                    {translate('profileImage.uploadNewImage')}
                                </Button>
                            </label>

                            {imageUrl && (
                                <Button className={classes.deleteCurrent} onClick={deleteCurrent}>
                                    {translate('profileImage.deleteCurrent')}
                                </Button>
                            )}
                        </>
                    ) : (
                        <>
                            <AvatarEditor
                                className={classes.editor}
                                ref={editor}
                                image={image}
                                width={width}
                                height={width}
                                border={canvasBorder}
                                color={[255, 255, 255, 0.8]} // RGBA
                                scale={scale}
                                rotate={rotation}
                            />
                            <div className={classes.imageActions}>
                                <IconButton onClick={scaleImage(1)}>
                                    <ZoomInIcon />
                                </IconButton>

                                <IconButton onClick={scaleImage(-1)}>
                                    <ZoomOutIcon />
                                </IconButton>

                                <IconButton onClick={rotate}>
                                    <RotateIcon />
                                </IconButton>

                                <div className={classes.flex} />

                                <Button onClick={cancelEdit} className={classes.cancelButton}>
                                    {translate('profileImage.cancel')}
                                </Button>

                                <Button onClick={onClickSave} color="primary" variant="outlined">
                                    {translate('profileImage.save')}
                                </Button>
                            </div>
                        </>
                    )}
                </div>
            </PortalModal>

            <Button className={classnames(classes.imageButton, { [classes.noImage]: !imageUrl })} onClick={openModal}>
                {imageUrl && <Image src={imageUrl} alt="" className={classes.image} />}
                <div className={classes.addOverlay}>
                    <AddImageIcon className={classes.addIcon} />
                </div>
            </Button>
        </>
    );
};

ProfileImageEditor.propTypes = {
    onSave: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
    width: PropTypes.number,
    currentImageId: PropTypes.number,
};

ProfileImageEditor.defaultProps = {
    width: 250,
    currentImageId: null,
};

export default ProfileImageEditor;
